<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <div class="label mb-2">Classroom</div>
        <v-autocomplete
          v-model="form.classroom_id"
          :items="classrooms"
          item-text="name"
          item-value="id"
          :loading="isClassroomListLoading"
          :rules="[validationRules.required]"
          :error="form.errors.classroom_id"
          :error-messages="form.errors.classroom_id"
          solo
          flat
          outlined
          dense
          placeholder="Choose classroom"
          @change="changeClassroom"
        />
        <v-row>
          <v-col cols="12" md="3">
            <div class="label mb-2">Class Date</div>
            <v-menu
              ref="classDateMenu"
              v-model="classDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="form.class_date"
                  :rules="[validationRules.required]"
                  :error="form.errors.class_date"
                  :error-message="form.errors.class_date"
                  placeholder="Select class date"
                  prepend-icon="mdi-calendar"
                  solo
                  flat
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.class_date"
                @change="$refs.classDateMenu.save(form.class_date)"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Class Time</div>
            <v-row align="center">
              <v-col cols="auto">
                <time-picker
                  v-model="form.start_time"
                  class="vue__time-picker--dense"
                  input-width="100px"
                  close-on-complete
                  manual-input
                  :disabled="isReadonly"
                />
              </v-col>
              <span>-</span>
              <v-col cols="auto">
                <time-picker
                  v-model="form.end_time"
                  class="vue__time-picker--dense"
                  input-width="100px"
                  close-on-complete
                  manual-input
                  :disabled="isReadonly"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Term</div>
            <v-text-field
              v-model="form.term"
              type="number"
              :rules="[validationRules.required]"
              :error="form.errors.term"
              :error-message="form.errors.term"
              solo
              flat
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Month</div>
            <v-text-field
              v-model="form.month"
              type="number"
              :rules="[validationRules.required]"
              :error="form.errors.month"
              :error-message="form.errors.month"
              solo
              flat
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Session</div>
            <v-text-field
              v-model="form.session"
              type="number"
              :rules="[validationRules.required]"
              :error="form.errors.session"
              :error-message="form.errors.session"
              solo
              flat
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-card
          v-if="
            selectedClassroom &&
            selectedClassroom.subscription_package.lesson_plan.is_templated
          "
          flat
        >
          <v-card-title>Study Materials</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-list flat>
              <template v-for="(lesson, i) in form.lessons">
                <v-list-item :key="i">
                  <v-list-item-content>
                    <v-container fluid class="pa-0">
                      <v-row>
                        <v-col cols="12" md="3">
                          <div class="label mb-2">Lesson</div>
                          <v-select
                            v-model="form.lessons[i]"
                            :items="filteredLessons"
                            return-object
                            :item-text="
                              (item) => bilingualText(item, 'name', 'name_cn')
                            "
                            solo
                            flat
                            outlined
                            dense
                            placeholder="Select lesson"
                          />
                        </v-col>
                        <v-col cols="12" md="3">
                          <div class="label mb-2">Category</div>
                          {{ getCategoryName(lesson.lesson_category_id) }}
                        </v-col>
                        <v-col cols="12" md="3">
                          <div class="label mb-2">Material File(s)</div>
                          <div v-if="lesson.attachment_file_url == null">-</div>
                          <ul v-else>
                            <li
                              v-for="(
                                attachment_url, j
                              ) in lesson.attachment_file_url.split(',')"
                              :key="j"
                            >
                              <a :href="attachment_url" target="_blank">
                                {{ filenameFromUrl(attachment_url) }}
                              </a>
                            </li>
                          </ul>
                        </v-col>
                        <v-col cols="12" md="3" class="text-right">
                          <v-btn
                            depressed
                            color="error"
                            @click="removeLesson(i)"
                          >
                            <v-icon left>mdi-delete-forever</v-icon> Delete
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-list-item-content>
                </v-list-item>
                <v-divider
                  v-if="i < form.lessons.length - 1"
                  :key="`div-${i}`"
                ></v-divider>
              </template>
              <v-list-item>
                <v-btn depressed color="success" @click="addLesson">
                  <v-icon left>mdi-plus-circle</v-icon> Add Lesson
                </v-btn>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
        <v-card v-if="mode != 'add'">
          <v-card-title>Study Progress</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-list flat>
              <template v-for="(studyProgress, i) in studentStudyProgresses">
                <v-list-item :key="studyProgress.student_id">
                  <v-list-item-content>
                    <v-container fluid class="pa-0">
                      <v-row align="center">
                        <v-col cols="12" md="2">
                          <h3>{{ studyProgress.student_name }}</h3>
                        </v-col>
                        <v-col cols="12" md="8">
                          <v-row v-if="studyProgress.item != null" dense>
                            <v-col
                              v-for="(progressDetail, j) in studyProgress.item
                                .details"
                              :key="`pd-${j}`"
                              cols="12"
                              md="3"
                              class="d-flex flex-wrap flex-column"
                            >
                              <h4>
                                {{
                                  getCategoryName(
                                    progressDetail.lesson_category_id
                                  )
                                }}
                              </h4>
                              <p>
                                {{ progressDetail.lesson_name }}
                                <br />
                                {{ progressDetail.lesson_name_cn }}
                              </p>
                              <v-spacer></v-spacer>
                              <div class="font-weight-bold">
                                <v-chip
                                  small
                                  label
                                  :color="
                                    scoreGradeProps(
                                      progressDetail.score_num,
                                      'color'
                                    )
                                  "
                                >
                                  Score: {{ progressDetail.score_grade }}
                                </v-chip>
                              </div>
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col cols="12" md="2" class="text-right">
                          <v-btn
                            v-if="studyProgress.item.id != null"
                            depressed
                            color="warning"
                            @click="editStudyProgress(studyProgress.student_id)"
                          >
                            <v-icon left>mdi-pencil</v-icon>
                            Edit Progress
                          </v-btn>
                          <v-btn
                            v-else
                            depressed
                            color="primary"
                            @click="addStudyProgress(studyProgress.student_id)"
                          >
                            <v-icon left>mdi-plus-circle</v-icon>
                            Add Progress
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-list-item-content>
                </v-list-item>
                <v-divider
                  v-if="i < studentStudyProgresses.length - 1"
                  :key="i"
                ></v-divider>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
    <study-progress-dialog
      v-if="studyProgressDialog.show"
      v-model="studyProgressDialog.show"
      :item="studyProgressDialog.item"
      :lessons="form.lessons"
      :categories="categories"
      submit-api
      @submit="onSubmitStudyProgress"
    ></study-progress-dialog>
  </v-card>
</template>
<script>
import StudyProgressDialog from '@components/ClassroomSchedule/StudyProgressDialog'
import constant from '@/constant'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'ClassroomScheduleForm',
  components: {
    StudyProgressDialog,
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    schedule: {
      type: Object,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      classDateMenu: false,
      form: this.$inertia.form({
        classroom_id: null,
        class_date: null,
        start_time: '',
        end_time: '',
        term: null,
        month: null,
        session: null,
        lessons: [],
        lesson_ids: [],
        study_progresses: [],
      }),
      studyProgressDialog: {
        item: {
          student_id: null,
          classroom_schedule_id: this.schedule ? this.schedule.id : null,
          notes: null,
          notes_cn: null,
          details: [],
        },
        show: false,
      },
    }
  },
  computed: {
    ...mapGetters({
      classroomList: 'classroom/list',
      isClassroomListLoading: 'classroom/isListLoading',
      lessonList: 'lesson/list',
      isLessonListLoading: 'lesson/isListLoading',
      categoryList: 'lessonCategory/list',
      isCategoryListLoading: 'lessonCategory/isListLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    classrooms() {
      return this.classroomList.data
    },
    selectedClassroom() {
      return this.form.classroom_id
        ? this.classrooms.find((e) => e.id === this.form.classroom_id)
        : null
    },
    lessons() {
      return this.lessonList.data
    },
    categories() {
      return this.categoryList.data
    },
    filteredLessons() {
      if (this.lessons.length === 0) {
        return []
      }
      return this.lessons.filter((e) => {
        let termValid = this.form.term ? this.form.term == e.term : true
        let monthValid = this.form.month ? this.form.month == e.month : true
        let sessionValid = this.form.session
          ? this.form.session == e.session
          : true
        return termValid && monthValid && sessionValid
      })
    },
    scoreGradeOptions() {
      return constant.scoreGradeOptions
    },
    students() {
      return this.schedule.classroom.students
    },
    studentStudyProgresses() {
      if (this.mode === 'add' || this.students.length == 0) {
        return []
      }
      const studyProgresses = this.form.study_progresses
      return this.students.map((student) => {
        const studyProgress = studyProgresses.find(
          (e) => e.student_id === student.id
        )
        return {
          student_id: student.id,
          student_name: student.name,
          item: studyProgress ?? {
            id: null,
            student_id: student.id,
            classroom_schedule_id: this.schedule.id,
            notes: null,
            notes_cn: null,
            details: this.schedule.lessons.map((e) => ({
              lesson_id: e.id,
              lesson_name: e.name,
              lesson_name_cn: e.name_cn,
              lesson_category_id: e.lesson_category_id,
              score_num: null,
              score_grade: null,
              notes: null,
              notes_cn: null,
            })),
          },
        }
      })
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getClassroomList: 'classroom/getList',
      getLessonList: 'lesson/getList',
      getCategoryList: 'lessonCategory/getList',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.schedule) {
        console.error(
          `schedule props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      if (this.mode != 'add' && this.schedule != null) {
        _.merge(this.form, this.schedule)
      }
      await Promise.all([this.getClassroomList(), this.getCategoryList()])
      if (this.selectedClassroom) {
        await this.getLessonList({
          lesson_plan_id:
            this.selectedClassroom.subscription_package.lesson_plan_id,
        })
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    async changeClassroom() {
      if (this.selectedClassroom) {
        await this.getLessonList({
          lesson_plan_id:
            this.selectedClassroom.subscription_package.lesson_plan_id,
        })
      }
    },
    getCategoryName(categoryId) {
      const category = this.categories.find((e) => e.id === categoryId)
      return category ? this.bilingualText(category, 'name', 'name_cn') : ''
    },
    removeLesson(index) {
      this.form.lessons.splice(index, 1)
    },
    addLesson() {
      this.form.lessons.push({
        id: null,
        name: null,
        name_cn: null,
        lesson_category_id: null,
        attachment_file_url: null,
      })
    },
    addStudyProgress(studentId) {
      this.studyProgressDialog.item.student_id = studentId
      this.studyProgressDialog.show = true
    },
    editStudyProgress(studentId) {
      const studyProgress = this.form.study_progresses.find(
        (e) => e.student_id === studentId
      )
      this.studyProgressDialog.item = _.cloneDeep(studyProgress)
      this.studyProgressDialog.show = true
    },
    resetStudyProgressDialog() {
      this.studyProgressDialog.item = {
        student_id: null,
        classroom_schedule_id: this.schedule.id,
        notes: null,
        notes_cn: null,
        details: [],
      }
      this.studyProgressDialog.show = false
    },
    onSubmitStudyProgress(value) {
      const index = this.form.study_progresses.findIndex(
        (e) => e.student_id === value.student_id
      )
      if (index === -1) {
        this.form.study_progresses.push(_.cloneDeep(value))
      } else {
        this.form.study_progresses[index] = _.cloneDeep(value)
      }
      this.resetStudyProgressDialog()
    },
    async submit() {
      if (this.$refs.form.validate()) {
        try {
          if (this.form.lessons.length > 0) {
            this.form.lesson_ids = this.form.lessons.map((e) => e.id)
          }
          if (this.mode === 'add') {
            await this.form.post('/classroom-schedules')
          } else if (this.mode === 'edit') {
            await this.form.put(`/classroom-schedules/${this.schedule.id}`)
          }
          this.$emit('submit')
        } catch (error) {
          this.handleError(error)
        }
      }
    },
  },
}
</script>
